/**
 * The object UI's two escape hatches (brief §9.1), by id: a custom input
 * widget inside the command shell, and a custom section on a record (full
 * width, after its tables; a lead section comes before them). Presentation
 * only: it names ids and declares nothing.
 *
 * The command ids are the ones packages D–I declare; until they do, an entry
 * whose command the manifest does not hold is simply never reached.
 */
import type { ComponentType } from "react";
import { parseCampaignPeopleCsv } from "@shared-core/domain/campaign-people-csv";
import type {
  ObjectId,
  RecordView,
} from "@shared-core/domain/object-ui/contract";
import { ArchivedArtworkSection } from "./components/domain/artwork/ArchivedArtworkSection";
import { OrderArtworkSection } from "./components/domain/artwork/OrderArtworkSection";
import { addKitsWidget } from "./components/domain/command-widgets/AddKitsWidget";
import { csvUploadWidget } from "./components/domain/command-widgets/CsvUploadWidget";
import { FeedbackImagesSection } from "./components/domain/feedback/FeedbackImagesSection";
import { SharedCommunicationSection } from "./components/domain/communications/SharedCommunicationSection";
import { deliveryAllocationWidget } from "./components/domain/command-widgets/DeliveryAllocationWidget";
import { despatchKitDropsWidget } from "./components/domain/command-widgets/DespatchKitDropsWidget";
import { itemConfigurationWidget } from "./components/domain/command-widgets/ItemConfigurationWidget";
import { lineVersionsWidget } from "./components/domain/command-widgets/LineVersionsWidget";
import { serviceParametersWidget } from "./components/domain/command-widgets/ServiceParametersWidget";
import { composeWidgets } from "./components/patterns/command-shell/compose-widgets";
import { MatchingSection } from "./components/domain/purchasing/MatchingSection";
import type {
  CustomWidget,
  CustomWidgetRegistry,
} from "./components/patterns/command-shell/types";

/**
 * A custom record section: reads the record it is on, renders nothing when it has nothing to say. `runCommand` opens
 * one of the record's own commands by id in the command shell, so a section's buttons are declared commands, never a
 * write of its own.
 */
export type RecordSection = ComponentType<{
  record: RecordView;
  runCommand?: (name: string) => void;
}>;

/**
 * Sections drawn first, under the lifecycle and before the record's tables: a data shape that is the record's main
 * content (the purchase invoice's Matching, review 13 §5, Ben 27 Oct 2026). A lead section draws no commands: every
 * command of the record is the header's (Ben, 28 Oct 2026).
 */
export interface LeadSection {
  Section: RecordSection;
}

export const RECORD_LEAD_SECTIONS: Partial<Record<ObjectId, LeadSection[]>> = {
  "finance_purchase_ledger.purchase_invoice": [{ Section: MatchingSection }],
};

export const RECORD_SECTIONS: Partial<Record<ObjectId, RecordSection[]>> = {
  // The files, findings, proofs and history sit on the version's own Artwork record; the order keeps its Artwork step
  // and one Artwork table whose rows open it (One C2).
  "artwork_preparation.artwork": [OrderArtworkSection],
  // The files the Tharstern artwork archive holds for each job of a Tharstern order, with a download (One package T3).
  "orders.tharstern_order": [ArchivedArtworkSection],
  "customer_communications.communication": [SharedCommunicationSection],
  // A note's screenshot and pasted images (feedback quick capture spec 01 §5): an image is a data shape, so it is a
  // custom section rather than a widget or a page.
  "feedback.feedback_item": [FeedbackImagesSection],
};

/** The catalogue item's line, drawn the same for Order now and Quote now: its specification, then where it goes. */
const itemLineWidgets = composeWidgets(
  itemConfigurationWidget,
  deliveryAllocationWidget,
);

/**
 * Add catalogue item… on a draft quote or an order picks its item in the shell's fields, and Add to draft quote… picks
 * its customer and draft quote there, so the same line is drawn after them: the pickers first, then the specification
 * and where it goes.
 */
const pickedItemLineWidgets: CustomWidget = {
  ...itemLineWidgets,
  placement: "after_fields",
};

export const COMMAND_WIDGETS: CustomWidgetRegistry = {
  "catalogue.add_to_draft_quote": pickedItemLineWidgets,
  "catalogue.add_catalogue_item_to_draft_quote": pickedItemLineWidgets,
  "catalogue.set_draft_quote_line_quantity_options": lineVersionsWidget,
  "catalogue.add_bespoke_line_to_draft_quote": deliveryAllocationWidget,
  "catalogue.add_catalogue_item_to_order": pickedItemLineWidgets,
  "catalogue.add_bespoke_line_to_order": deliveryAllocationWidget,
  "catalogue.order_catalogue_item_now": itemLineWidgets,
  "catalogue.quote_catalogue_item_now": itemLineWidgets,
  "orders.add_kits_to_order": addKitsWidget,
  "orders.record_kit_drops_despatched": despatchKitDropsWidget,
  "catalogue.add_service_extra_to_order": serviceParametersWidget,
  "catalogue.change_order_line_deliveries": deliveryAllocationWidget,
  "marketing_campaigns.confirm_people_upload": csvUploadWidget(
    parseCampaignPeopleCsv,
  ),
};
